/**
 * GdprPage.tsx — /legal/gdpr — GDPR & data-processing statement
 *
 * Purpose:    Controller/processor roles, GDPR rights, and the exact in-app
 *             flows for data export (Settings → Export my data → /v1/me/export)
 *             and right-to-erasure (Settings → Delete account), plus the
 *             sub-processor summary and data location.
 * Inputs:     Static content
 * Outputs:    Legal document page
 * Constraints: Standard SaaS template, marked "review with counsel".
 * SOT:        nsentry SaaS completion spec § G-WEB (legal)
 */
import { Link } from 'react-router-dom'
import { LegalLayout } from './LegalLayout'

export function GdprPage() {
  return (
    <LegalLayout title="GDPR & Data Processing" lastUpdated="2026-07-03">
      <section>
        <h2 id="roles">1. Roles: controller and processor</h2>
        <ul>
          <li>
            <strong>Your account data</strong> (email, name, billing): we are the{' '}
            <strong>data controller</strong>.
          </li>
          <li>
            <strong>Data you send to the Service</strong> (error events, RUM metrics,
            monitor targets, status-page subscriber emails): you are the controller and we
            act as your <strong>data processor</strong>, processing it only on your
            instructions to run the Service.
          </li>
        </ul>
        <p>
          This page, together with the <Link to="/legal/privacy">Privacy Policy</Link> and{' '}
          <Link to="/legal/terms">Terms of Service</Link>, records those processing
          commitments during early access. A signable Data Processing Agreement mirroring
          the ɳSelf master DPA (
          <a href="https://nself.org/legal/dpa">nself.org/legal/dpa</a>) is available on
          request.
        </p>
      </section>

      <section>
        <h2 id="rights">2. Your GDPR rights</h2>
        <p>
          Under Articles 15–21 GDPR you have the right of access, rectification, erasure,
          restriction, data portability, and objection. The two most-used rights are
          self-service in the app:
        </p>
      </section>

      <section>
        <h2 id="export">3. Exporting your data (portability)</h2>
        <ul>
          <li>Sign in and open <strong>Settings</strong> (<code>/app/settings</code>).</li>
          <li>
            Click <strong>&quot;Export my data&quot;</strong>. The app calls{' '}
            <code>GET /v1/me/export</code> and downloads your complete tenant dataset —
            profile, monitors, incidents, status pages, subscribers, API-key metadata, and
            telemetry summaries — as a machine-readable JSON file.
          </li>
          <li>No waiting period: the export is generated on demand.</li>
        </ul>
      </section>

      <section>
        <h2 id="erasure">4. Deleting your account (right to erasure)</h2>
        <ul>
          <li>Sign in and open <strong>Settings</strong> → <strong>Danger zone</strong>.</li>
          <li>
            Click <strong>&quot;Delete account&quot;</strong> and confirm by typing your
            account email. This permanently deletes your tenant and all its data —
            monitors, incidents, status pages, subscribers, error events, RUM data, and
            API keys.
          </li>
          <li>
            Deletion is immediate at the application layer; encrypted backups age out
            within 30 days. Minimal billing records are retained only as long as tax law
            requires.
          </li>
          <li>
            Lost access to your account? Email{' '}
            <a href="mailto:privacy@nself.org">privacy@nself.org</a> from the account
            address and we will verify and process the request within 30 days.
          </li>
        </ul>
      </section>

      <section>
        <h2 id="processing">5. Processing details</h2>
        <ul>
          <li>
            <strong>Location:</strong> primary storage on Hetzner servers in Germany (EU);
            CDN/edge delivery via Cloudflare and Vercel.
          </li>
          <li>
            <strong>Sub-processors:</strong> Stripe, ElasticEmail, Hetzner, Cloudflare,
            Vercel — full table with purposes in the{' '}
            <Link to="/legal/privacy">Privacy Policy</Link>.
          </li>
          <li>
            <strong>Security:</strong> TLS in transit, encryption at rest, tenant-scoped
            row isolation on every query, hashed passwords (Argon2id), and audit logging.
          </li>
          <li>
            <strong>Breach notification:</strong> we notify affected customers without
            undue delay and within 72 hours of becoming aware of a personal-data breach.
          </li>
          <li>
            <strong>Retention:</strong> per-tier windows described in the{' '}
            <Link to="/legal/privacy">Privacy Policy</Link> § Retention.
          </li>
        </ul>
      </section>

      <section>
        <h2 id="contact">6. Contact</h2>
        <p>
          Data-protection contact: <a href="mailto:privacy@nself.org">privacy@nself.org</a>.
          You may also lodge a complaint with your local supervisory authority.
        </p>
      </section>
    </LegalLayout>
  )
}
